import React, { useEffect, useCallback } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Login from "../views/Login";
import Home from "../views/Home";
import Dashboard from "../views/Dashboard";
import Editor from "../views/Editor";
import Resources from "../views/Resources";
import ResourceEditor from "../views/Resources/Editor";
import AddResource from "../views/AddResource";
import ReportView from "../views/ReportView";
// import Authenticate from '../views/Login/Authenticate';
// import ProtectedRoute from '../views/ProtectedRoute';
import { getCurrentUserAsync } from "../redux/userSlice";
import Groups from "../views/Groups";
import AuthorizedRoute from "./AuthorizedRoute";
import ScheduleReports from "../views/ScheduleReports";
import ScheduleLogsTable from "../views/ScheduleReports/ScheduleLogsTable";
import CreateSchedule from "../views/ScheduleReports/CreateSchedule";
// Standalone share page, superseded by the Share dialog on the logs page.
// import ShareReportForm from "../views/ScheduleReports/ShareReportForm";
import ReportHtmlView from "../views/ReportHtmlView";
import ForgetPassword from "../views/ForgetPassword/ForgetPassword";
import OTPVerify from "../views/OTPVerification/OTPVerify";
import CreateNewPwd from "../views/NewPassword/CreateNewPwd";
import DashboardView from "../views/DashboardView";
import Setting from "../views/Settings/Setting";
import Support from "../views/Supports/Support";
import DashboardsList from "../views/DashboardList/DashboardList";
import CreateDashboard from "../views/CreateDashboard";
import ErrorBoundary from "../components/ErrorBoundary";

function AppRoutes() {
  const dispatch = useDispatch();
  const userdata = useSelector((state) => state.user);

  const fetchCurrentUser = useCallback(() => {
    dispatch(getCurrentUserAsync());
  }, [dispatch]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  return (
    <Router>
      <Switch>
        <Route path="/login">{userdata.user && !userdata.user.isSigned ? <Login /> : <Redirect to="/" />}</Route>
        <Route exact path="/forget-password">
          <ForgetPassword />
        </Route>
        <Route exact path="/create-new-password">
          <CreateNewPwd />
        </Route>
        <Route exact path="/otp-verify">
          <OTPVerify />
        </Route>
        {/* The editor is wrapped so an unexpected payload shows a message instead of
            unmounting the whole app to a blank page. */}
        <Route exact path="/editor/:id/">
          <DashboardView noMargin={true}>
            <ErrorBoundary message="We could not open the editor for this report. This is usually caused by unexpected data coming back from the server.">
              <Editor />
            </ErrorBoundary>
          </DashboardView>
        </Route>
        <Route exact path="/editor/:id/:selectedId">
          <DashboardView noMargin={true}>
            <ErrorBoundary message="We could not open the editor for this report. This is usually caused by unexpected data coming back from the server.">
              <Editor />
            </ErrorBoundary>
          </DashboardView>
        </Route>

        {/* Resource editor.

            Create and edit are one page — the route says which, and the
            connection type decides the layout (endpoint builder for REST /
            GraphQL, connection form for the data sources). Both are declared
            before the list, and the list is `exact`, or /resources would
            swallow them.

            The previous flow is left in place: /add-resource still asks for the
            application first and still opens the antd modal forms. */}
        <AuthorizedRoute path={`/resources/new`} isAuthorized={true || (userdata.user && userdata.user.isSigned)}>
          <ErrorBoundary fullPage message="We could not open the resource editor. Please reload and try again.">
            <ResourceEditor />
          </ErrorBoundary>
        </AuthorizedRoute>

        <AuthorizedRoute
          path={`/resources/:resourceId/edit`}
          isAuthorized={true || (userdata.user && userdata.user.isSigned)}
        >
          <ErrorBoundary fullPage message="We could not open the resource editor. Please reload and try again.">
            <ResourceEditor />
          </ErrorBoundary>
        </AuthorizedRoute>

        <Route exact path="/resources">
          <Resources />
        </Route>

        {/* <ProtectedRoute path="/resources" component={Resources} /> */}
        <Route path="/add-resource">
          <AddResource />
          {/* {(userdata.user && userdata.user.isSigned) ?
            <Resources />
            : <Redirect to="/login" />
          } */}
        </Route>

        <AuthorizedRoute path={`/groups`} isAuthorized={true || (userdata.user && userdata.user.isSigned)}>
          <Groups />
        </AuthorizedRoute>

        <AuthorizedRoute path={`/create-dashboard`} isAuthorized={true || (userdata.user && userdata.user.isSigned)}>
          <ErrorBoundary fullPage message="We could not open the create form. Please reload and try again.">
            <CreateDashboard />
          </ErrorBoundary>
        </AuthorizedRoute>

        <AuthorizedRoute path={`/dashboard-list`} isAuthorized={true || (userdata.user && userdata.user.isSigned)}>
          <ErrorBoundary fullPage message="We could not load your dashboards. Please reload and try again.">
            <DashboardsList />
          </ErrorBoundary>
        </AuthorizedRoute>

        {/* Scheduled Reports.

            Create and Edit are one page — the route only says which mode it is
            in, so there is a single component and no duplicated form. The two
            of them are declared before the list, and the list is `exact`, or
            /scheduled-reports would swallow both of them.

            Old paths kept as redirects: /scheduling and /create-schedule are in
            bookmarks and in the sidebar's menu-key map. */}
        <AuthorizedRoute
          path={`/scheduled-reports/create`}
          isAuthorized={true || (userdata.user && userdata.user.isSigned)}
        >
          <CreateSchedule />
        </AuthorizedRoute>

        <AuthorizedRoute
          path={`/scheduled-reports/edit/:scheduleId`}
          isAuthorized={true || (userdata.user && userdata.user.isSigned)}
        >
          <CreateSchedule />
        </AuthorizedRoute>

        <AuthorizedRoute
          path={`/scheduled-reports/logs/:scheduleId`}
          isAuthorized={true || (userdata.user && userdata.user.isSigned)}
        >
          <ScheduleLogsTable />
        </AuthorizedRoute>

        <AuthorizedRoute
          exact
          path={`/scheduled-reports`}
          isAuthorized={true || (userdata.user && userdata.user.isSigned)}
        >
          <ScheduleReports />
        </AuthorizedRoute>

        <Route exact path="/scheduling">
          <Redirect to="/scheduled-reports" />
        </Route>

        <Route exact path="/create-schedule">
          <Redirect to="/scheduled-reports/create" />
        </Route>

        <AuthorizedRoute path={`/settings-page`} isAuthorized={true || (userdata.user && userdata.user.isSigned)}>
          <Setting />
        </AuthorizedRoute>

        <AuthorizedRoute path={`/support-page`} isAuthorized={true || (userdata.user && userdata.user.isSigned)}>
          <Support />
        </AuthorizedRoute>

        {/* Sharing a report is a dialog on the Schedule Logs page now, not a
            page of its own — see ShareReportModal. Route kept commented rather
            than removed, in case the standalone form is wanted back. */}
        {/* <AuthorizedRoute
          path={`/share-schedule-report`}
          isAuthorized={true || (userdata.user && userdata.user.isSigned)}
        >
          <ShareReportForm />
        </AuthorizedRoute> */}

        {/* Old logs path, from before the page took a schedule id. */}
        <Route exact path="/schedule-logs">
          <Redirect to="/scheduled-reports" />
        </Route>

        <Route path="/report-view">
          <ReportView />
        </Route>

        <Route path="/board/:boardId">
          <ErrorBoundary fullPage message="We could not display this dashboard. This is usually caused by unexpected data coming back from the server.">
            <Dashboard />
          </ErrorBoundary>
        </Route>
        <Route path="/report/:boardId">
          <ErrorBoundary fullPage message="We could not display this report. This is usually caused by unexpected data coming back from the server.">
            <Dashboard />
          </ErrorBoundary>
        </Route>

        {/* Drill-down targets open here, with parameters from the query string, so it is
            the most exposed page in the app - it must degrade rather than blank out. */}
        <AuthorizedRoute path="/report-html-view/:boardId" isAuthorized={true}>
          <ErrorBoundary fullPage message="We could not display this report. This is usually caused by unexpected data coming back from the server.">
            <ReportHtmlView />
          </ErrorBoundary>
        </AuthorizedRoute>

        <Route path="/">
          {userdata.user && userdata.user.isSigned ? (
            <DashboardView>
              <Home />
            </DashboardView>
          ) : (
            <Redirect to="/login" />
          )}
        </Route>
      </Switch>
    </Router>
  );
}

export default AppRoutes;
